import {
    DnsWriteError,
    DnsWriteErrorKind,
} from '../types';

// FNP-1191 CRUD half — TURNING A GraphQL REJECTION BACK INTO A CLASS.
//
// ⚠️ WHY THIS FILE EXISTS AT ALL.
//
// The API classifies (FNP-1180: `DnsProviderErrorKind` is a closed union) and
// then GraphQL flattens it. The dns-read Lambda lets a `DnsProviderError`
// propagate uncaught; the resolver's `util.appendError(error.message,
// error.type, result)` puts the class name in `errorType` and the message in
// `message`, and the message was built by the contract's base constructor as
// `${kind}: ${providerMessage}`.
//
// So by the time it reaches the browser the kind is present TWICE, in two
// string shapes, and neither is a field called `kind`:
//
//   errorType: 'DnsZoneNotEmptyError'
//   message:   'ZONE_NOT_EMPTY: The hosted zone contains resource records
//               that are not SOA or NS records.'
//
// Recovering the class is therefore string work, and doing it at ONE boundary
// is the point. The alternative — every call site regex-matching a message —
// is how three call sites end up disagreeing about what counts as
// ZONE_NOT_EMPTY, and it puts provider-shaped strings into UI components.
//
// ⚠️ THE MESSAGE PREFIX IS READ FIRST, NOT THE errorType. The prefix is
// produced by the contract's own constructor and is the same string as the
// union member, so it survives a Lambda bundler renaming a class. `errorType`
// is the fallback, matched on the class name the contract declares.

const KINDS: DnsWriteErrorKind[] = [
    'AUTHENTICATION_FAILED',
    'PERMISSION_DENIED',
    'NOT_FOUND',
    'RATE_LIMITED',
    'PROVIDER_UNAVAILABLE',
    'VALIDATION_REJECTED',
    'ZONE_NOT_EMPTY',
];

// The contract's class names, which is what `errorType` carries.
const ERROR_TYPE_TO_KIND: Record<string, DnsWriteErrorKind> = {
    DnsAuthenticationFailedError: 'AUTHENTICATION_FAILED',
    DnsPermissionDeniedError: 'PERMISSION_DENIED',
    DnsNotFoundError: 'NOT_FOUND',
    DnsRateLimitedError: 'RATE_LIMITED',
    DnsProviderUnavailableError: 'PROVIDER_UNAVAILABLE',
    DnsValidationRejectedError: 'VALIDATION_REJECTED',
    DnsZoneNotEmptyError: 'ZONE_NOT_EMPTY',
};

// Amplify rejects with a GraphQLResult-shaped object carrying an `errors`
// array; a transport failure rejects with a plain Error. Both arrive here as
// `unknown` under strict mode, so nothing is assumed about the shape.
type GraphQLErrorLike = {
    message?: unknown,
    errorType?: unknown,
}

const errorsOf = (e: unknown): GraphQLErrorLike[] => {
    if (e && typeof e === 'object' && 'errors' in e) {
        const {errors} = e as { errors?: unknown };
        if (Array.isArray(errors)) {
            return errors as GraphQLErrorLike[];
        }
    }
    return [];
};

const asString = (value: unknown): string =>
    typeof value === 'string' ? value : '';

// ⚠️ ANCHORED AT THE START, and the delimiter is required.
//
// A bare `message.includes('ZONE_NOT_EMPTY')` would also match a provider whose
// own prose mentions the phrase, and — worse — would match
// 'VALIDATION_REJECTED: ... not ZONE_NOT_EMPTY ...'. The contract builds the
// message as `${kind}: ${providerMessage}`, so the prefix is the only position
// that is actually a claim about the class.
const kindFromMessage = (message: string): DnsWriteErrorKind | undefined =>
    KINDS.find(kind => message.startsWith(`${kind}: `) || message === kind);

// Strips the `KIND: ` prefix so the human sees the provider's sentence and not
// a machine token they cannot act on. The kind is rendered separately, as a
// label — classify for the core, preserve for the human, BOTH.
const messageWithoutPrefix = (message: string, kind: DnsWriteErrorKind): string => {
    const prefix = `${kind}: `;
    return message.startsWith(prefix) ? message.slice(prefix.length) : message;
};

/**
 * Classify a rejected zone mutation.
 *
 * Always returns a value — an unrecognisable failure is `UNKNOWN` with whatever
 * text was available, never a thrown error of its own. A classifier that can
 * itself fail turns a delete refusal into a blank screen.
 */
export const classifyWriteError = (e: unknown): DnsWriteError => {

    const graphQLErrors = errorsOf(e);

    for (const graphQLError of graphQLErrors) {

        const message = asString(graphQLError.message);
        const errorType = asString(graphQLError.errorType);

        // The message prefix first — it is the contract's own string.
        const fromMessage = kindFromMessage(message);
        if (fromMessage) {
            return {
                kind: fromMessage,
                providerMessage: messageWithoutPrefix(message, fromMessage),
            };
        }

        // Then the class name in errorType.
        const fromType = ERROR_TYPE_TO_KIND[errorType];
        if (fromType) {
            return {
                kind: fromType,
                providerMessage: message,
            };
        }
    }

    // Nothing recognisable. Surface the best text available rather than
    // inventing copy: the first GraphQL error's message, else a plain Error's,
    // else nothing at all.
    const firstMessage = asString(graphQLErrors[0]?.message);
    const plainMessage = e instanceof Error ? e.message : '';

    return {
        kind: 'UNKNOWN',
        providerMessage: firstMessage || plainMessage,
    };
};

export const isZoneNotEmpty = (e: unknown): boolean =>
    classifyWriteError(e).kind === 'ZONE_NOT_EMPTY';
